import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { QRCodeSVG } from 'qrcode.react'
import { ShieldCheck, ClipboardCopy, Download, AlertTriangle } from 'lucide-react'
import { authService } from '../../services/auth.service'
import type { TwoFactorSetupResponse } from '../../types'

type Step = 'idle' | 'enroll' | 'recovery' | 'disabling'

/**
 * Two-factor authentication (TOTP) management, rendered in the profile
 * Security tab. Self-contained: status, enrollment (QR + confirm),
 * one-time recovery-code display, and disable (password + code).
 */
export default function TwoFactorSettings() {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()

  const [step, setStep] = useState<Step>('idle')
  const [setupData, setSetupData] = useState<TwoFactorSetupResponse | null>(null)
  const [enrollCode, setEnrollCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
  const [disablePassword, setDisablePassword] = useState('')
  const [disableCode, setDisableCode] = useState('')
  const [error, setError] = useState('')

  const { data: status, isLoading } = useQuery({
    queryKey: ['twofa', 'status'],
    queryFn: () => authService.getTwoFactorStatus(),
  })

  const refreshStatus = () => {
    queryClient.invalidateQueries({ queryKey: ['twofa', 'status'] })
  }

  const setupMutation = useMutation({
    mutationFn: () => authService.setupTwoFactor(),
    onSuccess: (data) => {
      setSetupData(data)
      setEnrollCode('')
      setError('')
      setStep('enroll')
    },
    onError: (err: any) => setError(err.response?.data?.message || err.message),
  })

  const enableMutation = useMutation({
    mutationFn: (code: string) => authService.enableTwoFactor(code),
    onSuccess: (data) => {
      setRecoveryCodes(data.recoveryCodes)
      refreshStatus()
      setStep('recovery')
    },
    onError: (err: any) => setError(err.response?.data?.message || err.message),
  })

  const disableMutation = useMutation({
    mutationFn: () => authService.disableTwoFactor(disablePassword, disableCode.trim()),
    onSuccess: () => {
      setDisablePassword('')
      setDisableCode('')
      setError('')
      refreshStatus()
      setStep('idle')
      toast.success(t('auth.twofa.disabledToast'))
    },
    onError: (err: any) => setError(err.response?.data?.message || err.message),
  })

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(t('auth.twofa.copied'))
    } catch {
      toast.error(t('auth.twofa.copyFailed'))
    }
  }

  const downloadRecoveryCodes = () => {
    const blob = new Blob(
      [recoveryCodes.join('\n')],
      { type: 'text/plain' },
    )
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'ekubur-recovery-codes.txt'
    link.click()
    URL.revokeObjectURL(url)
  }

  const errorBanner = error && (
    <div className="bg-red-50 border border-red-200 rounded-md p-3">
      <p className="text-sm text-red-600">{error}</p>
    </div>
  )

  const inputClasses =
    'mt-1 block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm'

  if (isLoading) {
    return (
      <div key={i18n.language} className="text-sm text-neutral-500">
        {t('common.loading')}
      </div>
    )
  }

  return (
    <div key={i18n.language} className="max-w-md">
      <div className="flex items-center space-x-2 mb-2">
        <ShieldCheck className="h-5 w-5 text-primary-600" />
        <h3 className="text-lg font-semibold text-neutral-900">
          {t('auth.twofa.title')}
        </h3>
        {status?.enabled && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {t('auth.twofa.status.enabled')}
          </span>
        )}
      </div>
      <p className="text-sm text-neutral-600 mb-4">{t('auth.twofa.description')}</p>

      {/* Idle: either an Enable CTA or the Disable entry point */}
      {step === 'idle' && !status?.enabled && (
        <div className="space-y-4">
          <button
            type="button"
            disabled={setupMutation.isPending}
            onClick={() => setupMutation.mutate()}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {setupMutation.isPending ? t('common.loading') : t('auth.twofa.enable')}
          </button>
          {errorBanner}
        </div>
      )}

      {step === 'idle' && status?.enabled && (
        <div className="space-y-4">
          <p className="text-xs text-neutral-500">{t('auth.twofa.recovery.hint')}</p>
          <button
            type="button"
            onClick={() => {
              setError('')
              setStep('disabling')
            }}
            className="px-4 py-2 bg-neutral-100 text-neutral-700 border border-neutral-300 rounded-md hover:bg-neutral-200 focus:outline-none focus:ring-2 focus:ring-neutral-400"
          >
            {t('auth.twofa.disable')}
          </button>
          {errorBanner}
        </div>
      )}

      {/* Enrollment: QR + manual secret + confirm code */}
      {step === 'enroll' && setupData && (
        <div className="space-y-4">
          <div className="flex flex-col items-center p-4 bg-white border border-neutral-200 rounded-lg">
            <div className="p-3 bg-white border border-neutral-200 rounded-md">
              <QRCodeSVG value={setupData.otpauthUrl} size={180} />
            </div>
            <p className="mt-3 text-xs text-neutral-500">
              {t('auth.twofa.setup.manualEntry')}
            </p>
            <div className="mt-1 flex items-center space-x-2">
              <code className="px-2 py-1 bg-neutral-100 rounded font-mono text-sm break-all">
                {setupData.secret}
              </code>
              <button
                type="button"
                onClick={() => copyToClipboard(setupData.secret)}
                className="p-1.5 text-neutral-500 hover:text-primary-600"
                title={t('auth.twofa.copied')}
              >
                <ClipboardCopy className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700">
              {t('auth.twofa.step.code')}
            </label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={enrollCode}
              onChange={(e) => {
                setEnrollCode(e.target.value)
                if (error) setError('')
              }}
              placeholder="000000"
              className={inputClasses}
            />
          </div>

          {errorBanner}

          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => {
                setStep('idle')
                setSetupData(null)
                setError('')
              }}
              className="px-4 py-2 text-neutral-600 hover:text-neutral-800"
            >
              {t('auth.twofa.step.back')}
            </button>
            <button
              type="button"
              disabled={enableMutation.isPending || enrollCode.length !== 6}
              onClick={() => enableMutation.mutate(enrollCode.trim())}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {enableMutation.isPending ? t('common.loading') : t('auth.twofa.setup.confirm')}
            </button>
          </div>
        </div>
      )}

      {/* Recovery codes — shown exactly once, right after enabling */}
      {step === 'recovery' && (
        <div className="space-y-4">
          <div className="flex items-start space-x-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-800">{t('auth.twofa.recovery.warning')}</p>
          </div>

          <div className="grid grid-cols-2 gap-2 p-4 bg-neutral-50 border border-neutral-200 rounded-lg">
            {recoveryCodes.map((c) => (
              <code key={c} className="font-mono text-sm text-neutral-800">
                {c}
              </code>
            ))}
          </div>

          <div className="flex justify-between">
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => copyToClipboard(recoveryCodes.join('\n'))}
                className="inline-flex items-center px-3 py-2 text-sm border border-neutral-300 rounded-md hover:bg-neutral-50 text-neutral-700"
              >
                <ClipboardCopy className="h-4 w-4 mr-1.5" />
                {t('auth.twofa.recovery.copyAll')}
              </button>
              <button
                type="button"
                onClick={downloadRecoveryCodes}
                className="inline-flex items-center px-3 py-2 text-sm border border-neutral-300 rounded-md hover:bg-neutral-50 text-neutral-700"
              >
                <Download className="h-4 w-4 mr-1.5" />
                {t('auth.twofa.recovery.download')}
              </button>
            </div>
            <button
              type="button"
              onClick={() => setStep('idle')}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              {t('auth.twofa.recovery.done')}
            </button>
          </div>
        </div>
      )}

      {/* Disable: re-authenticate with password + code */}
      {step === 'disabling' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700">
              {t('auth.twofa.disableFlow.password')}
            </label>
            <input
              type="password"
              value={disablePassword}
              onChange={(e) => {
                setDisablePassword(e.target.value)
                if (error) setError('')
              }}
              required
              className={inputClasses}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700">
              {t('auth.twofa.disableFlow.code')}
            </label>
            <input
              type="text"
              value={disableCode}
              onChange={(e) => {
                setDisableCode(e.target.value)
                if (error) setError('')
              }}
              placeholder={t('auth.twofa.disableFlow.codePlaceholder')}
              required
              className={inputClasses}
            />
          </div>

          {errorBanner}

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => {
                setStep('idle')
                setDisablePassword('')
                setDisableCode('')
                setError('')
              }}
              className="px-4 py-2 text-neutral-600 hover:text-neutral-800"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              disabled={disableMutation.isPending || !disablePassword || !disableCode}
              onClick={() => disableMutation.mutate()}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
            >
              {disableMutation.isPending ? t('common.loading') : t('auth.twofa.disableFlow.confirm')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
